import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { quotesApi } from '../api';
import { PhoneInput } from './PhoneInput';

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls = 'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';
const labelCls = 'text-xs text-gray-400 mb-1 block';
const sectionCls = 'text-[11px] font-semibold text-[#D4AF37] uppercase tracking-wider';

const empty = {
  title: '', customer_name: '', customer_phone: '', customer_email: '',
  quote_date: new Date().toISOString().slice(0, 10), valid_until: '', default_price_level: 'standard',
  address_line1: '', address_line2: '', city: '', state: '', zip_code: '',
  scope_summary: '', exclusions: '', terms: '', internal_notes: '',
};

export const QuoteFormDialog = ({ open, onOpenChange, quote, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const isEdit = !!quote;

  useEffect(() => {
    if (open) {
      setForm(quote ? Object.fromEntries(Object.keys(empty).map((k) => [k, quote[k] ?? ''])) : { ...empty });
    }
  }, [open, quote]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.title.trim()) return toast.error('Quote title is required');
    if (!form.customer_name.trim()) return toast.error('Customer name is required');
    if (!form.quote_date) return toast.error('Quote date is required');
    setSaving(true);
    const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === '' ? null : v]));
    try {
      const r = isEdit ? await quotesApi.update(quote.id, payload) : await quotesApi.create(payload);
      toast.success(isEdit ? 'Quote updated' : 'Quote created');
      onOpenChange(false);
      onSaved?.(r.data);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Could not save quote');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-[#111111] border-gray-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-form-dialog">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Quote' : 'New Quote'}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="space-y-3">
            <div className={sectionCls}>Quote</div>
            <div>
              <label className={labelCls}>Title *</label>
              <Input value={form.title} onChange={set('title')} placeholder="e.g. 123 Main Street Renovation" className={inputCls} data-testid="quote-title-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className={labelCls}>Quote Date *</label><Input type="date" value={form.quote_date} onChange={set('quote_date')} className={inputCls} data-testid="quote-date-input" /></div>
              <div><label className={labelCls}>Valid Until</label><Input type="date" value={form.valid_until || ''} onChange={set('valid_until')} className={inputCls} data-testid="quote-valid-until-input" /></div>
            </div>
          </div>
          <div className="space-y-3">
            <div className={sectionCls}>Customer</div>
            <div><label className={labelCls}>Customer Name *</label><Input value={form.customer_name} onChange={set('customer_name')} className={inputCls} data-testid="quote-customer-name-input" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className={labelCls}>Phone</label><PhoneInput value={form.customer_phone || ''} onChange={(v) => setForm((f) => ({ ...f, customer_phone: v }))} className={inputCls} data-testid="quote-customer-phone-input" /></div>
              <div><label className={labelCls}>Email</label><Input value={form.customer_email || ''} onChange={set('customer_email')} className={inputCls} data-testid="quote-customer-email-input" /></div>
            </div>
          </div>
          <div className="space-y-3">
            <div className={sectionCls}>Job Location</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className={labelCls}>Address Line 1</label><Input value={form.address_line1 || ''} onChange={set('address_line1')} className={inputCls} data-testid="quote-address1-input" /></div>
              <div><label className={labelCls}>Address Line 2</label><Input value={form.address_line2 || ''} onChange={set('address_line2')} className={inputCls} data-testid="quote-address2-input" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className={labelCls}>City</label><Input value={form.city || ''} onChange={set('city')} className={inputCls} data-testid="quote-city-input" /></div>
              <div><label className={labelCls}>State</label><Input value={form.state || ''} onChange={set('state')} className={inputCls} data-testid="quote-state-input" /></div>
              <div><label className={labelCls}>ZIP</label><Input value={form.zip_code || ''} onChange={set('zip_code')} className={inputCls} data-testid="quote-zip-input" /></div>
            </div>
          </div>
          <div className="space-y-3">
            <div className={sectionCls}>Content</div>
            <div><label className={labelCls}>Scope Summary</label><Textarea value={form.scope_summary || ''} onChange={set('scope_summary')} rows={3} className={inputCls} data-testid="quote-scope-input" /></div>
            <div><label className={labelCls}>Exclusions</label><Textarea value={form.exclusions || ''} onChange={set('exclusions')} rows={2} className={inputCls} data-testid="quote-exclusions-input" /></div>
            <div><label className={labelCls}>Terms</label><Textarea value={form.terms || ''} onChange={set('terms')} rows={2} className={inputCls} data-testid="quote-terms-input" /></div>
          </div>
          <div className="space-y-3">
            <div className={sectionCls}>Internal</div>
            <div><label className={labelCls}>Internal Notes (never shown to customer)</label><Textarea value={form.internal_notes || ''} onChange={set('internal_notes')} rows={2} className={inputCls} data-testid="quote-internal-notes-input" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300" data-testid="quote-form-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="quote-form-save-btn">
              {saving && <Loader2 size={14} className="animate-spin" />} {isEdit ? 'Save Changes' : 'Create Quote'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
